import { differenceInMilliseconds } from 'date-fns';
import { generatePath } from 'react-router';
import { createSelector } from 'reselect';

import { MESSAGE_TYPE } from 'constants/ChatConstants';
import { PATH } from 'constants/RoutesConstants';
import { PRIVATE } from 'constants/VisibilityConstants';
import { ENTITY_TYPE } from 'modules/app/constants';
import { getMessageIdsById } from 'modules/interventions/selectors/base';
import { getCommunicationBlockingTags } from 'modules/tags/utils';
import { canAccessTo } from 'modules/users/selectors/currentUser';
import { getCustomerById } from 'selectors/customerSelectors';
import { getSelectedDealerId } from 'selectors/dealerSelectors';
import { getProps } from 'utils/selectors';
import { getLink } from 'utils/TimelineUtils';

import { getMessageEntityMapping, paramsFormatter } from '../utils';
import { getContextSelectEntries, getLatestContext, getSelectedContext } from './context.select';
import { getConversationById, getSelectedConversationId } from './dashboard';
import { getState, getUIState } from './general';
import { getSelectedHistory } from './history.select';

const getRoot = state => state;

const sortBySentOn = (a, b) => differenceInMilliseconds(a.sentOn, b.sentOn);

const reduceMessages = ({ messageIds = [] } = {}, messages = {}) =>
  messageIds.reduce((acc, id) => (messages[id] ? acc.concat(messages[id]) : acc), []).sort(sortBySentOn);

const getDetailsState = createSelector(getUIState, state => state.details);

export const isLoading = createSelector(getDetailsState, state => state.isLoading);

export const isPosting = createSelector(getDetailsState, state => state.isPosting);

export const getMessagesPage = createSelector(getDetailsState, state => state.page);

export const getMessagesLinks = createSelector(getDetailsState, state => state.links);

const getAllMessages = createSelector(getState, state => state.messages);

export const getMessageById = createSelector(getAllMessages, getProps, (messages, id) => messages[id]);

const getConversationCustomerId = createSelector(getConversationById, conversation => conversation?.customerId);

export const getConversationCustomer = createSelector(getRoot, getConversationCustomerId, getCustomerById);

export const getAllMessagesByConversationId = createSelector(getConversationById, getAllMessages, reduceMessages);

export const getAllMessagesByInterventionId = createSelector(getMessageIdsById, getAllMessages, (messageIds, state) =>
  reduceMessages({ messageIds }, state));

const getConversationIdArg = (_state, _interventionId, conversationId) => conversationId;

/**
 * Messages of a single file (intervention) that belong to a given conversation.
 *
 * A conversation is shared across every file of the same customer, so scoping the in-file chat by
 * `conversationId` alone leaks sibling files' messages (e.g. a duplicated file showing the source
 * file's messages until a hard refresh). We therefore start from the per-file message ids
 * ({@link getMessageIdsById}) and split them by conversation, which keeps both conversations of a
 * 2-contacts file separate while never showing another file's messages.
 *
 * @type {(state: unknown, interventionId?: string, conversationId?: string) => import("../reducer/data/messages").StoredMessage[]}
 */
export const getFileMessagesByConversationId = createSelector(
  getMessageIdsById,
  getAllMessages,
  getConversationIdArg,
  (messageIds, messages, conversationId) =>
    reduceMessages({ messageIds }, messages).filter(message => message.conversationId === conversationId)
);

const getConversations = createSelector(getState, state => state.conversations);

const getCampaignFileIdArg = (_state, campaignFileId) => campaignFileId;

const getConversationIdAfterFileArg = (_state, _campaignFileId, conversationId) => conversationId;

/**
 * Messages of a single campaign file that belong to a given conversation.
 *
 * Same problem as {@link getFileMessagesByConversationId} for interventions: a conversation is
 * shared across every file of the same customer, so a duplicated campaign file would otherwise show
 * the source file's messages until a hard refresh. Campaign files have no per-file message-ids
 * store, so we start from the conversation's messages and keep only those tagged with this file's
 * `campaignFileId`.
 *
 * @type {(state: unknown, campaignFileId?: string, conversationId?: string) => import("../reducer/data/messages").StoredMessage[]}
 */
export const getCampaignFileMessagesByConversationId = createSelector(
  getConversations,
  getAllMessages,
  getCampaignFileIdArg,
  getConversationIdAfterFileArg,
  (conversations, messages, campaignFileId, conversationId) =>
    reduceMessages(conversations?.[conversationId], messages).filter(
      message => message.campaignFileId === campaignFileId
    )
);

/**
 * @param {import("../reducer/data/messages").StoredMessage[]} messages
 * @returns {Pick<import("../reducer/data/messages").StoredMessage, 'channel'>}
 */
export const getDefaultChannel = messages => {
  const publicMessages = messages.filter(({ isCustomer, visibility }) => !isCustomer && visibility !== PRIVATE);
  const lastMessage = publicMessages.pop();
  return lastMessage?.channel;
};

export const getDefaultChannelForChat = createSelector(getAllMessagesByConversationId, getDefaultChannel);

/** @type {(state: unknown, interventionId?: string, conversationId?: string) => import("../reducer/data/messages").StoredMessage['channel']} */
export const getDefaultChannelForFileConversation = createSelector(getFileMessagesByConversationId, getDefaultChannel);

/** @type {(state: unknown, campaignFileId?: string, conversationId?: string) => import("../reducer/data/messages").StoredMessage['channel']} */
export const getDefaultChannelForCampaignFileConversation = createSelector(
  getCampaignFileMessagesByConversationId,
  getDefaultChannel
);

export const getLastMessageForIntervention = createSelector(
  getAllMessagesByInterventionId,
  messages => messages[messages.length - 1]
);

export const getLatestMessageByConversationId = createSelector(getAllMessagesByConversationId, messages =>
  messages.filter(({ type = MESSAGE_TYPE.MESSAGE }) => type === MESSAGE_TYPE.MESSAGE).pop());

export const isReady = createSelector(getDetailsState, state => state.isReady);

export const getDetailsFilters = createSelector(getUIState, state => state.filters);

export const getActiveEntity = createSelector(
  getSelectedHistory,
  getSelectedContext,
  (selectedHistory, selectedContext) => selectedHistory ?? selectedContext
);

const canAccessToCustomers = createSelector(getRoot, () => generatePath(PATH.CUSTOMERS.path), canAccessTo);

export const getEntityLink = createSelector(
  getConversationCustomer,
  getLatestContext,
  canAccessToCustomers,
  (customer, mostRecentContext, hasCustomerAccess = false) => {
    if (hasCustomerAccess && customer?.id) {
      return generatePath(PATH.CUSTOMERS_DETAILED.path, { customerId: customer.id });
    }
    const { id: entityId, module, appointmentId, mode, campaignId } = mostRecentContext ?? {};
    const isCampaign = Boolean(campaignId);

    return (
      Boolean(entityId) &&
      getLink({
        entityId: appointmentId ?? entityId,
        module,
        mode,
        isCampaign,
        isAppointment: Boolean(appointmentId),
        search: isCampaign ? { text: customer?.displayName } : undefined,
      })
    );
  }
);

export const getTargetCustomer = createSelector(
  getConversationCustomer,
  getSelectedContext,
  (customer, context) => context?.customerInfo ?? customer
);

export const getConversationSocketParameters = createSelector(getSelectedDealerId, entityId => ({
  entityType: ENTITY_TYPE.DEALER,
  entityId,
}));

export const getConversationMessagesRequestParams = createSelector(
  getSelectedHistory,
  getSelectedConversationId,
  (history, conversationId) => ({ conversationId, ...getMessageEntityMapping(paramsFormatter(history, conversationId)) })
);

export const sendConversationMessageRequestParams = createSelector(
  getSelectedContext,
  getSelectedConversationId,
  (context, conversationId) => getMessageEntityMapping(paramsFormatter(context, conversationId))
);

const getMediaDrawerState = createSelector(getDetailsState, state => state.mediaDrawer);

export const mediaDrawerIsOpen = createSelector(getMediaDrawerState, state => state.open);

export const getMediaDrawerContextId = createSelector(getMediaDrawerState, state => state.contextId);

export const getDisplayedContexts = createSelector(
  getSelectedHistory,
  getContextSelectEntries,
  (history, contexts) => (history ? [history] : contexts)
);

export const getTagsFromActiveEntity = createSelector(
  getActiveEntity,
  (entity) => entity?.tags ?? []
);

export const getTagsBlockingOutgoingCommunication = createSelector(
  getTagsFromActiveEntity,
  getCommunicationBlockingTags
);
